import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Stack, Tabs, Title } from '@mantine/core';

import useEnsisQuery from '../../hooks/useEnsisQuery';
import { CenteredLoader, SelectProposalResources } from '../../components';
import ProposalDetails from './ProposalDetails';
import OpportunityFilesSection from './OpportunityFilesSection';
import ProposalOutlineWithDocumentViewer from '../../components/ProposalOutlineWithDocumentViewer';
import ProposalFactsSection from './ProposalFactsSection';
import { AdditionalProposalInfo } from './AdditionalProposalInfo';
import { formatSectionsMetadata } from '../../utils/apiUtils';
import useEnsisMutation from '../../hooks/useEnsisMutation';
import { getProposalManagementRoute } from '../../utils/routerUtils';
import { type ResourceFilterState } from '../../types/miscTypes';
import {
  DEFAULT_RESOURCE_FILTER_STATE
} from '../../utils/constants';

const FILTER_BY_IN_USE_STATE: ResourceFilterState = {
  ...DEFAULT_RESOURCE_FILTER_STATE,
  inUse: true
};

const ManageProposalPage: React.FC = () => {
  const { organization, proposalUid, proposalManagementTab = 'outline' } = useParams();
  const navigate = useNavigate();
  const { data, isLoading } = useEnsisQuery(`/app/proposals/${proposalUid}/data`);
  const opportunityUid = data?.opportunity.uid;
  const referenceUids = data?.references?.filter(
    (resource) => resource.processing_status !== 'FAILED'
  ).map(
    (resource) => resource.uid
  ) ?? [];

  const sections = formatSectionsMetadata(data?.sections) ?? [];

  const editProposalResources = useEnsisMutation(`/app/proposals/${proposalUid}/data`, {
    requestType: 'patch',
    successMessage: 'Resources updated',
    showSuccessMessage: false,
    queryKeysToInvalidate: [`/app/proposals/${proposalUid}/data`]
  });

  const handleUpdateReferences = useCallback((uids: string[]) => {
    editProposalResources.mutate({
      reference_uids: uids
    });
  }, [editProposalResources.mutate]);

  if (isLoading || data === undefined) {
    return <CenteredLoader />;
  }

  const proposalManagementRoute = getProposalManagementRoute(organization, proposalUid);

  return (
    <Stack gap={0} flex={1}>
      <Title order={6} ml={16}> Proposal Management </Title>
      <Tabs
        flex={1}
        defaultValue='outline'
        value={proposalManagementTab}
        onChange={(value) => {
          navigate(`${proposalManagementRoute}/${value}`, { replace: true });
        }}
      >
        <Tabs.List mih={46}>
          <Tabs.Tab value='details'>
            Proposal Details
          </Tabs.Tab>
          <Tabs.Tab value='opportunityFiles'>
            Opportunity Files
          </Tabs.Tab>
          <Tabs.Tab value='resources'>
            Resources
          </Tabs.Tab>
          <Tabs.Tab value='outline'>
            Outline
          </Tabs.Tab>
          <Tabs.Tab value='facts'>
            Facts
          </Tabs.Tab>
          <Tabs.Tab value='additionalInfo'>
            Win Themes / Differentiators
          </Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value='details'>
          <ProposalDetails proposal={data} />
        </Tabs.Panel>
        <Tabs.Panel value='opportunityFiles'>
          <OpportunityFilesSection opportunityUid={opportunityUid ?? ''} />
        </Tabs.Panel>
        <Tabs.Panel value='resources'>
          <SelectProposalResources

            referenceUids={referenceUids}
            onUpdateReferences={handleUpdateReferences}
            defaultFilter={FILTER_BY_IN_USE_STATE}
            allowResourceUpload
          />
        </Tabs.Panel>
        <Tabs.Panel value='outline'>
          <ProposalOutlineWithDocumentViewer
            style={{ flex: 1 }}
            proposal={data}
            sections={sections}
            requirementRemovalType='UNASSIGN'
          />
        </Tabs.Panel>
        <Tabs.Panel value='facts'>
          <ProposalFactsSection />
        </Tabs.Panel>
        <Tabs.Panel value='additionalInfo'>
          <AdditionalProposalInfo
            additionalContext={data?.additional_context}
            tabIsFocused={proposalManagementTab === 'additionalInfo'}
          />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
};

export default ManageProposalPage;
